
"use client";

import React from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  ChartType,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Add these components for the blog post stats
export const ChartContainer = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={`w-full ${className}`}>{children}</div>
);

export const ChartTooltip = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-white p-2 rounded shadow-lg border border-slate-200">{children}</div>
);

export const ChartTooltipContent = ({ children }: { children: React.ReactNode }) => (
  <div className="text-sm">{children}</div>
);

const defaultOptions: ChartOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      grid: {
        color: "#e2e8f0",
      },
      beginAtZero: true,
    },
  },
};

interface ChartProps<TType extends ChartType> {
  data: ChartData<TType>;
  options?: ChartOptions<TType>;
  className?: string;
}

export function BarChart({ data, options, className }: ChartProps<'bar'>) {
  const mergedOptions = { ...defaultOptions, ...options } as ChartOptions<'bar'>;
  
  return (
    <div className={className}>
      <Bar data={data as ChartData<'bar'>} options={mergedOptions} />
    </div>
  );
}

export function LineChart({ data, options, className }: ChartProps<'line'>) {
  const mergedOptions = { ...defaultOptions, ...options } as ChartOptions<'line'>;
  
  return (
    <div className={className}>
      <Line data={data as ChartData<'line'>} options={mergedOptions} />
    </div>
  );
}

export function PieChart({ data, options, className }: ChartProps<'pie'>) {
  const mergedOptions = {
    ...defaultOptions,
    scales: undefined, // Remove scales for pie charts
    ...options,
  } as ChartOptions<'pie'>;
  
  return (
    <div className={className}>
      <Pie data={data as ChartData<'pie'>} options={mergedOptions} />
    </div>
  );
}
